<template>
  <div>
    <!-- Toolbar -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <el-input
          v-model="search"
          placeholder="Buscar por asunto..."
          suffix-icon="el-icon-search"
          size="small"
          clearable
          style="width: 280px;"
          @input="handleSearch"
        />
      </div>
      <el-button type="primary" size="small" @click="showCompose = true">
        <i class="el-icon-message"></i> Nueva Notificación
      </el-button>
    </div>

    <!-- Skeleton loading -->
    <SkeletonTheme v-if="loading" color="#eef0f4" highlight="#f7f8fa">
      <div
        v-for="i in 4"
        :key="i"
        class="notification-card mb-3"
        style="pointer-events:none;"
      >
        <b-row align-v="center">
          <b-col xs="12" sm="7">
            <div class="d-flex align-items-center">
              <Skeleton circle height="10px" width="10px" class="mr-2" />
              <div style="flex:1;">
                <Skeleton height="14px" width="60%" class="mb-1" />
                <Skeleton height="11px" width="35%" />
              </div>
            </div>
          </b-col>
          <b-col xs="6" sm="2" class="text-center">
            <Skeleton height="20px" width="55px" style="border-radius:4px;" />
          </b-col>
          <b-col xs="6" sm="2" class="text-center">
            <Skeleton height="13px" width="80px" />
          </b-col>
          <b-col xs="12" sm="1" class="text-center">
            <Skeleton height="18px" width="18px" />
          </b-col>
        </b-row>
      </div>
    </SkeletonTheme>

    <!-- Lista de notificaciones -->
    <div v-else-if="notifications.length" class="notification-list">
      <div
        v-for="notif in notifications"
        :key="notif.id"
        class="notification-card mb-3"
        @click="viewNotification(notif)"
      >
        <b-row align-v="center">
          <b-col xs="12" sm="7">
            <div class="d-flex align-items-center">
              <span
                class="status-dot mr-2"
                :class="`status-${notif.status}`"
              ></span>
              <div>
                <strong class="d-block">{{ notif.subject }}</strong>
                <small class="text-muted">
                  {{ formatDate(notif.created_at) }}
                  <span class="ml-2" v-if="notif.sent_at">
                    · Enviado {{ formatDate(notif.sent_at) }}
                  </span>
                </small>
              </div>
            </div>
          </b-col>
          <b-col xs="6" sm="2" class="text-center">
            <span class="badge" :class="channelBadge(notif.channel)">
              {{ channelLabel(notif.channel) }}
            </span>
          </b-col>
          <b-col xs="6" sm="2" class="text-center">
            <span class="text-muted" style="font-size: 13px;">
              {{ notif.sent_count || 0 }}/{{ notif.total_recipients }}
              destinatarios
            </span>
          </b-col>
          <b-col xs="12" sm="1" class="text-center" @click.stop>
            <el-dropdown
              trigger="click"
              @command="handleCommand($event, notif)"
            >
              <i
                class="el-icon-more"
                style="cursor:pointer;font-size:18px;"
              ></i>
              <el-dropdown-menu slot="dropdown" class="notif-dropdown">
                <el-dropdown-item command="view">
                  <i class="el-icon-view"></i> Ver detalle
                </el-dropdown-item>
                <el-dropdown-item
                  command="send"
                  v-if="notif.status === 'draft'"
                >
                  <i class="el-icon-s-promotion"></i> Enviar
                </el-dropdown-item>
                <el-dropdown-item
                  command="resend"
                  v-if="notif.status === 'failed'"
                >
                  <i class="el-icon-refresh"></i> Reintentar envío
                </el-dropdown-item>
                <el-dropdown-item
                  command="cancel"
                  divided
                  v-if="notif.status === 'sending'"
                >
                  <i class="el-icon-close notif-icon-warn"></i>
                  <span class="notif-text-warn">Cancelar envío</span>
                </el-dropdown-item>
                <el-dropdown-item command="delete" divided>
                  <i class="el-icon-delete notif-icon-danger"></i>
                  <span class="notif-text-danger">Eliminar</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="wrapper__card-empty-documents d-flex flex-column justify-content-center align-items-center p-4 m-4"
    >
      <div class="empty-icon-circle">
        <i class="el-icon-message"></i>
      </div>
      <div class="wrapper__card-empty-info my-2">
        <p class="text-center">
          Aún no has enviado notificaciones a tus clientes.
        </p>
      </div>
      <div class="wrapper__card-empty-action">
        <el-button type="primary" plain @click="showCompose = true">
          Crear primera notificación
        </el-button>
      </div>
    </div>

    <!-- Drawer: Crear / Editar (reutiliza BulkMessageModal) -->
    <BulkMessageModal
      :visible.sync="showCompose"
      :recipients="[]"
      :selectableMode="true"
      :availableClients="clients"
      context="clients"
      :whatsappEnabled="whatsappEnabled"
      @sent="onMessageSent"
    />

    <!-- Drawer: Ver detalle -->
    <el-drawer
      :visible.sync="showDetail"
      direction="rtl"
      size="420px"
      append-to-body
      :with-header="false"
    >
      <div v-if="detailNotification" class="notif-detail-wrapper">
        <div class="notif-detail-header">
          <h5 class="notif-detail-title">
            {{ detailNotification.subject || "Sin asunto" }}
          </h5>
          <div class="notif-detail-actions">
            <button
              v-if="detailNotification.status === 'failed'"
              class="notif-detail-btn notif-detail-btn--retry"
              title="Reintentar envío"
              @click="resendFromDetail"
            >
              <i class="el-icon-refresh"></i>
            </button>
            <button
              class="notif-detail-btn notif-detail-btn--danger"
              title="Eliminar notificación"
              @click="deleteFromDetail"
            >
              <i class="el-icon-delete"></i>
            </button>
            <button class="notif-detail-btn" @click="showDetail = false">
              <i class="el-icon-close"></i>
            </button>
          </div>
        </div>
        <div style="padding: 20px; overflow-y: auto; flex: 1;">
          <div class="d-flex align-items-center mb-3" style="gap: 12px;">
            <span class="badge" :class="statusBadge(detailNotification.status)">
              {{ statusLabel(detailNotification.status) }}
            </span>
            <span
              class="badge"
              :class="channelBadge(detailNotification.channel)"
            >
              {{ channelLabel(detailNotification.channel) }}
            </span>
            <small class="text-muted">
              {{ formatDate(detailNotification.created_at) }}
            </small>
          </div>
          <div
            class="notification-preview mb-3 p-3"
            v-html="detailNotification.body"
          ></div>
          <h6 class="font-semi-bold">
            Destinatarios ({{ detailNotification.total_recipients }})
          </h6>
          <div
            v-for="r in detailNotification.recipients || []"
            :key="r.id"
            class="recipient-row d-flex align-items-center mb-1"
          >
            <span class="status-dot mr-2" :class="`status-${r.status}`"></span>
            <span>{{ r.client ? r.client.name : "—" }}</span>
            <small class="text-muted ml-2">
              {{ r.client ? r.client.email : "" }}
            </small>
            <small v-if="r.error_message" class="text-danger ml-auto">
              {{ r.error_message }}
            </small>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import notificationService from "../../services/notificationService";
import businessService from "../../services/businessService";
import BulkMessageModal from "../../../reports/components/BulkMessageModal";
import Api from "@/config/Api";
import moment from "moment";
import { Skeleton, SkeletonTheme } from "vue-loading-skeleton";

export default {
  name: "NotificationListComponent",
  components: { BulkMessageModal, Skeleton, SkeletonTheme },
  data: () => ({
    notifications: [],
    clients: [],
    search: "",
    loading: true,
    showCompose: false,
    showDetail: false,
    detailNotification: null,
    editingId: null,
    saving: false,
    sending: false,
    whatsappEnabled: false,
    form: {
      subject: "",
      body: "",
      channel: "email",
      client_ids: []
    }
  }),
  methods: {
    async loadNotifications() {
      this.loading = true;
      try {
        const res = await notificationService.getNotifications({
          search: this.search
        });
        if (res.data.success) {
          this.notifications = res.data.data.data || res.data.data;
        }
      } catch (e) {
        return false;
      } finally {
        this.loading = false;
      }
    },

    async loadClients() {
      try {
        let page = 1;
        let lastPage = 1;
        let all = [];

        do {
          const res = await businessService.getBusiness({
            params: { page, per_page: 100 }
          });
          const { clients, pagination } = res.data || {};
          if (Array.isArray(clients)) {
            all = all.concat(clients);
          }
          lastPage = pagination?.last_page ?? 1;
          page++;
        } while (page <= lastPage);

        this.clients = all.map(c => ({
          id: c.id,
          name: c.name || c.business_name || "",
          email: c.email || null,
          phone: c.mobile || c.phone || null
        }));
      } catch (e) {
        return false;
      }
    },

    async loadWhatsappStatus() {
      try {
        const res = await Api().get("/whatsapp/config");
        const config = res?.data?.data;
        this.whatsappEnabled = !!(config && config.status === "active");
      } catch (e) {
        this.whatsappEnabled = false;
      }
    },

    handleSearch() {
      clearTimeout(this._searchTimeout);
      this._searchTimeout = setTimeout(() => this.loadNotifications(), 400);
    },

    selectAllClients() {
      this.form.client_ids = this.clients.map(c => c.id);
    },

    onMessageSent() {
      this.showCompose = false;
      this.loadNotifications();
    },

    resetForm() {
      this.form = {
        subject: "",
        body: "",
        channel: "email",
        client_ids: []
      };
      this.editingId = null;
    },

    async saveDraft() {
      if (!this.form.subject || !this.form.client_ids.length) {
        this.$message.warning(
          "Completa el asunto y selecciona al menos un destinatario."
        );
        return;
      }
      this.saving = true;
      try {
        let res;
        if (this.editingId) {
          res = await notificationService.updateNotification(
            this.editingId,
            this.form
          );
        } else {
          res = await notificationService.createNotification(this.form);
        }
        if (res.data.success) {
          this.$notifications.success({ message: res.data.message });
          this.showCompose = false;
          this.resetForm();
          await this.loadNotifications();
        }
      } catch (e) {
        return false;
      } finally {
        this.saving = false;
      }
    },

    async saveAndSend() {
      if (
        !this.form.subject ||
        !this.form.body ||
        !this.form.client_ids.length
      ) {
        this.$message.warning(
          "Completa todos los campos y selecciona destinatarios."
        );
        return;
      }
      this.sending = true;
      try {
        // Crear si es nuevo
        let notifId = this.editingId;
        if (!notifId) {
          const res = await notificationService.createNotification(this.form);
          if (res.data.success) {
            notifId = res.data.data.id;
          }
        } else {
          await notificationService.updateNotification(notifId, this.form);
        }
        // Enviar
        if (notifId) {
          const sendRes = await notificationService.sendNotification(notifId);
          if (sendRes.data.success) {
            this.$notifications.success({ message: sendRes.data.message });
          }
        }
        this.showCompose = false;
        this.resetForm();
        await this.loadNotifications();
      } catch (e) {
        return false;
      } finally {
        this.sending = false;
      }
    },

    async viewNotification(notif) {
      try {
        const res = await notificationService.getNotification(notif.id);
        if (res.data.success) {
          this.detailNotification = res.data.data;
          this.showDetail = true;
        }
      } catch (e) {
        return false;
      }
    },

    async handleCommand(command, notif) {
      if (command === "view") {
        this.viewNotification(notif);
      } else if (command === "send") {
        try {
          await this.$confirm(
            `¿Enviar "${notif.subject}" a ${notif.total_recipients} destinatarios?`,
            "Confirmar envío",
            { confirmButtonText: "Enviar", cancelButtonText: "Cancelar" }
          );
          const res = await notificationService.sendNotification(notif.id);
          if (res.data.success) {
            this.$notifications.success({ message: res.data.message });
            await this.loadNotifications();
          }
        } catch (e) {
          return false;
        }
      } else if (command === "resend") {
        try {
          await this.$confirm(
            `¿Reintentar el envío de "${notif.subject}" a ${notif.total_recipients} destinatarios?`,
            "Reintentar envío",
            { confirmButtonText: "Reintentar", cancelButtonText: "Cancelar" }
          );
          const res = await notificationService.sendNotification(notif.id);
          if (res.data.success) {
            this.$notifications.success({ message: res.data.message });
            await this.loadNotifications();
          }
        } catch (e) {
          return false;
        }
      } else if (command === "cancel") {
        try {
          await this.$confirm(
            `¿Cancelar el envío de "${notif.subject}"? El envío se detendrá.`,
            "Cancelar envío",
            {
              confirmButtonText: "Sí, cancelar",
              cancelButtonText: "No",
              type: "warning"
            }
          );
          const res = await notificationService.deleteNotification(notif.id);
          if (res.data.success) {
            this.$notifications.success({ message: "Envío cancelado." });
            await this.loadNotifications();
          }
        } catch (e) {
          return false;
        }
      } else if (command === "delete") {
        try {
          await this.$confirm("¿Eliminar esta notificación?", "Confirmar", {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning"
          });
          const res = await notificationService.deleteNotification(notif.id);
          if (res.data.success) {
            this.$notifications.success({ message: res.data.message });
            await this.loadNotifications();
          }
        } catch (e) {
          return false;
        }
      }
    },

    async resendFromDetail() {
      if (!this.detailNotification) return;
      try {
        await this.$confirm(
          `¿Reintentar el envío a ${this.detailNotification.total_recipients} destinatarios?`,
          "Reintentar envío",
          { confirmButtonText: "Reintentar", cancelButtonText: "Cancelar" }
        );
        const res = await notificationService.sendNotification(
          this.detailNotification.id
        );
        if (res.data.success) {
          this.$notifications.success({ message: res.data.message });
          this.showDetail = false;
          await this.loadNotifications();
        }
      } catch (e) {
        return false;
      }
    },

    async deleteFromDetail() {
      if (!this.detailNotification) return;
      try {
        await this.$confirm("¿Eliminar esta notificación?", "Confirmar", {
          confirmButtonText: "Eliminar",
          cancelButtonText: "Cancelar",
          type: "warning"
        });
        const res = await notificationService.deleteNotification(
          this.detailNotification.id
        );
        if (res.data.success) {
          this.$notifications.success({ message: res.data.message });
          this.showDetail = false;
          this.detailNotification = null;
          await this.loadNotifications();
        }
      } catch (e) {
        return false;
      }
    },

    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY HH:mm") : "";
    },

    channelLabel(ch) {
      return (
        { email: "Email", whatsapp: "WhatsApp", both: "Email + WA" }[ch] || ch
      );
    },
    channelBadge(ch) {
      return (
        {
          email: "badge-info",
          whatsapp: "badge-success",
          both: "badge-primary"
        }[ch] || "badge-secondary"
      );
    },
    statusLabel(st) {
      return (
        {
          draft: "Borrador",
          sending: "Enviando...",
          sent: "Enviado",
          failed: "Fallido"
        }[st] || st
      );
    },
    statusBadge(st) {
      return (
        {
          draft: "badge-secondary",
          sending: "badge-warning",
          sent: "badge-success",
          failed: "badge-danger"
        }[st] || "badge-secondary"
      );
    }
  },
  mounted() {
    this.loadNotifications();
    this.loadClients();
    this.loadWhatsappStatus();
  }
};
</script>

<style scoped>
.notification-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 14px 18px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.notification-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.status-draft {
  background: #909399;
}
.status-pending {
  background: #e6a23c;
}
.status-sending {
  background: #e6a23c;
}
.status-sent {
  background: #67c23a;
}
.status-failed {
  background: #f56c6c;
}
.notification-preview {
  background: #f8f9fb;
  border-radius: 6px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 14px;
}
.recipient-row {
  padding: 4px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;
}
.notif-detail-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.notif-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff;
  flex-shrink: 0;
}
.notif-detail-title {
  font-size: 15px;
  font-weight: 700;
  color: #31355c;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin-right: 12px;
}
.notif-detail-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.notif-detail-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: #909399;
  transition: all 0.15s;
  &:hover {
    background: #f3f4f6;
    color: #31355c;
  }
}
.notif-detail-btn--retry {
  &:hover {
    background: #eff6ff;
    color: #3b82f6;
  }
}
.notif-detail-btn--danger {
  &:hover {
    background: #fff1f0;
    color: #f56c6c;
  }
}
</style>

<style>
/* Dropdown de notificaciones */
.notif-dropdown {
  min-width: 160px !important;
  padding: 4px 0 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid #e4eaf6 !important;
}
.notif-dropdown .el-dropdown-menu__item {
  font-size: 13px;
  padding: 8px 16px;
  line-height: 1;
  display: block;
  color: #4b5563;
  white-space: nowrap;
}
.notif-dropdown .el-dropdown-menu__item i {
  font-size: 13px;
  margin-right: 7px;
  vertical-align: middle;
}
.notif-dropdown .el-dropdown-menu__item span {
  vertical-align: middle;
}
.notif-dropdown .el-dropdown-menu__item:hover {
  background: #f3f4f6;
  color: #31355c;
}
/* Sección dividida — resetear el ::before para que no rompa el layout */
.notif-dropdown .el-dropdown-menu__item--divided {
  margin-top: 0;
  border-top: 1px solid #f0f0f0;
}
.notif-dropdown .el-dropdown-menu__item--divided::before {
  display: none;
}
.notif-dropdown .notif-icon-warn {
  color: #e6a23c;
}
.notif-dropdown .notif-text-warn {
  color: #e6a23c;
  vertical-align: middle;
}
.notif-dropdown .notif-icon-danger {
  color: #f56c6c;
}
.notif-dropdown .notif-text-danger {
  color: #f56c6c;
  vertical-align: middle;
}
</style>
